import { useEffect, useState, useSyncExternalStore } from 'react';
import { CONFIG } from '../config';
import type { SyncEngine, SyncStatus } from '../sync/SyncEngine';
import { deviceId } from '../runtime/monitor';
import type { StorageInfo } from '../storage/AssetStore';

export function useSyncStatus(sync: SyncEngine): SyncStatus {
  return useSyncExternalStore(
    (cb) => sync.subscribe(cb),
    () => sync.status,
  );
}

const mb = (b?: number) => (b == null ? '—' : `${(b / 1e6).toLocaleString('es-PE', { maximumFractionDigits: 1 })} MB`);
const when = (t: number | null) => (t ? new Date(t).toLocaleString('es-PE', { hour12: false }) : '—');

/* ---------------- ¿Sigues ahí? ---------------- */

export function IdlePrompt({ onStay, onTimeout }: { onStay: () => void; onTimeout: () => void }) {
  const [left, setLeft] = useState(CONFIG.idlePromptSeconds);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (left <= 0) onTimeout();
  }, [left, onTimeout]);

  const R = 54;
  const C = 2 * Math.PI * R;
  return (
    <div className="fill a-fade z-40 flex items-center justify-center" style={{ background: 'rgba(14,20,38,0.72)', animationDuration: '200ms' }} onClick={onStay}>
      <div className="a-up flex flex-col items-center rounded-[40px] bg-blanco text-center" style={{ width: 840, padding: '64px 64px 56px', gap: 28 }}>
        <div className="relative" style={{ width: 132, height: 132 }}>
          <svg width="132" height="132" viewBox="0 0 132 132" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="66" cy="66" r={R} stroke="#DDE0E8" strokeWidth="10" fill="none" />
            <circle
              cx="66"
              cy="66"
              r={R}
              stroke="#1C294C"
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - Math.max(0, left) / CONFIG.idlePromptSeconds)}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="fill flex items-center justify-center font-bold text-primario" style={{ fontSize: 48 }}>
            {Math.max(0, left)}
          </div>
        </div>
        <div className="font-bold text-primario" style={{ fontSize: 60, lineHeight: 1.05 }}>
          ¿Sigues ahí?
        </div>
        <div className="text-primario/70" style={{ fontSize: 30 }}>
          Volveremos al inicio en unos segundos.
        </div>
        <button
          type="button"
          className="pressable flex items-center justify-center rounded-full bg-secundario font-bold text-primario"
          style={{ height: 104, width: '100%', fontSize: 32, marginTop: 12 }}
          onClick={(e) => {
            e.stopPropagation();
            onStay();
          }}
        >
          Sí, sigo aquí
        </button>
        <button
          type="button"
          className="pressable font-semibold text-primario/70"
          style={{ height: 72, fontSize: 26 }}
          onClick={(e) => {
            e.stopPropagation();
            onTimeout();
          }}
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );
}

/* ---------------- Primer arranque ---------------- */

export function FirstBoot({ sync }: { sync: SyncEngine }) {
  const s = useSyncStatus(sync);
  const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
  const downloading = s.phase === 'downloading' && s.total > 0;
  let detail = 'Conectando con el servidor…';
  if (s.phase === 'building') detail = 'Preparando la lista de imágenes…';
  if (downloading) detail = `Descargando ${s.done} de ${s.total} imágenes · ${mb(s.bytes)}`;
  if (s.phase === 'ready') detail = 'Listo. Abriendo…';
  if (s.phase === 'error') detail = s.offline ? 'Sin conexión a internet. Reintentando automáticamente…' : 'No se pudo completar la descarga. Reintentando automáticamente…';
  if (s.phase === 'error' && s.lastError?.includes('no tiene proyectos')) detail = 'Aún no hay proyectos publicados. Aparecerán aquí apenas se publiquen en el Studio.';

  return (
    <div className="screen flex flex-col items-center justify-center bg-primario text-center" data-screen="primer-arranque" style={{ padding: '0 120px', gap: 40 }}>
      <img src="/brand/logo-ontario-blanco.png" alt="Ontario" style={{ height: 120 }} draggable={false} />
      <div className="rounded bg-secundario" style={{ width: 120, height: 8, marginTop: 40 }} />
      <div className="font-bold text-blanco" style={{ fontSize: 64, lineHeight: 1.1 }}>
        Preparando contenido…
      </div>
      <div className="text-blanco/75" style={{ fontSize: 28, maxWidth: 760 }}>
        Estamos guardando las imágenes en el equipo para que todo funcione al instante, incluso sin internet. Esto ocurre solo la primera vez.
      </div>
      <div className="relative overflow-hidden rounded-full bg-blanco/15" style={{ width: 760, height: 16, marginTop: 24 }}>
        {downloading ? (
          <div className="absolute left-0 top-0 h-full rounded-full bg-secundario" style={{ width: `${Math.max(3, pct)}%`, transition: 'width 400ms ease' }} />
        ) : (
          <div className="absolute top-0 h-full rounded-full bg-secundario" style={{ left: 0, width: '40%', animation: 'indet 1.4s ease-in-out infinite' }} />
        )}
      </div>
      <div className={s.phase === 'error' ? 'font-semibold text-secundario' : 'text-blanco/80'} style={{ fontSize: 26, minHeight: 36 }}>
        {detail}
        {downloading ? ` · ${pct}%` : ''}
      </div>
    </div>
  );
}

/* ---------------- Panel técnico (pulsación larga de 3 s sobre el logo en Proyectos) ---------------- */

export function AdminPanel({ sync, onClose }: { sync: SyncEngine; onClose: () => void }) {
  const s = useSyncStatus(sync);
  const [info, setInfo] = useState<StorageInfo>({});
  useEffect(() => {
    void sync.store.info().then(setInfo);
  }, [sync, s.phase]);
  const rows: Array<[string, string]> = [
    ['Tótem', deviceId()],
    ['App', CONFIG.appVersion],
    ['Origen', CONFIG.source === 'sanity' ? `Sanity (${CONFIG.sanity.projectId}/${CONFIG.sanity.dataset})` : 'Demo local'],
    ['Almacenamiento', sync.store.kind === 'cache' ? 'Cache Storage (Plan A)' : 'Filesystem (Plan B)'],
    ['Versión activa', s.activeVersion || '—'],
    ['Versión pendiente', s.pendingVersion || '—'],
    ['Estado', `${s.phase}${s.phase === 'downloading' ? ` ${s.done}/${s.total}` : ''}`],
    ['Última consulta', when(s.lastCheckAt)],
    ['Última sincronización OK', when(s.lastSuccessAt)],
    ['Último error', s.lastError || '—'],
    ['Archivos faltantes', String(s.missingLive)],
    ['Uso / cuota', `${mb(info.usage)} / ${mb(info.quota)}`],
    ['Almacenamiento persistente', info.persisted == null ? '—' : info.persisted ? 'Sí' : 'No'],
  ];
  return (
    <div className="fill a-fade z-50 flex items-center justify-center" style={{ background: 'rgba(14,20,38,0.8)', animationDuration: '150ms' }}>
      <div className="flex flex-col rounded-[40px] bg-blanco" style={{ width: 920, padding: 56, gap: 28 }}>
        <div className="font-bold text-primario" style={{ fontSize: 44 }}>
          Panel técnico
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-primario/10" style={{ fontSize: 22, padding: '8px 0', gap: 24 }}>
              <span className="flex-none text-primario/60">{k}</span>
              <span className="break-all text-right font-semibold text-primario">{v}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2" style={{ gap: 16 }}>
          <AdminButton primary onClick={() => void sync.checkNow(true)}>
            Sincronizar ahora
          </AdminButton>
          <AdminButton onClick={() => location.reload()}>Recargar app</AdminButton>
          <AdminButton onClick={() => location.assign('/diagnostico')}>Diagnóstico</AdminButton>
          <AdminButton onClick={onClose}>Cerrar</AdminButton>
        </div>
      </div>
    </div>
  );
}

function AdminButton({ children, onClick, primary }: { children: React.ReactNode; onClick: () => void; primary?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pressable rounded-full font-bold ${primary ? 'bg-secundario text-primario' : 'border-2 border-primario/25 text-primario'}`}
      style={{ height: 80, fontSize: 26 }}
    >
      {children}
    </button>
  );
}
